import Swal from 'sweetalert2';
import { parseISO, isBefore, format } from 'date-fns';
import {
  HTML_AD_TYPES,
  VIDEO_TYPES,
  IMAGE_TYPES,
  VIDEO_TYPE,
  IMAGE_TYPE,
} from '../constant/constants';

const now = format(new Date(), 'yyyy-MM-dd');

const campaignFieldsMap = {
  name: 'Name',
  start_date: 'Start Date',
  end_date: 'End Date',
  total_budget: 'Budget',
  impressions_per_day: 'Max Impression / Day',
  impressions_per_user: 'Impression / User / Campaign',
  impressions_per_session: 'Impression / User / Session',
  clicks_per_user: 'Clicks / User / Campaign',
  clicks_per_sessions: 'Clicks / User / Session',
  targeted_states: 'Targeted Location',
  targeted_interests: 'Targeted Interest',
  targeted_feeds: 'Targeted Feed',
  targeted_telcos: 'Targeted Telco Provider',
  targeted_devices: 'Targeted Device',
  type: 'Type',
  impressions_per_time_block: 'Impression / User / Day',
  clicks_per_time_block: 'Clicks / User / Day',
  cpm: 'CPM',
  start_hour: 'Start Hour',
  ads_type: 'Ad Type',
};

const adFieldsMap = {
  name: 'Advertisement Name',
  body: 'Advertisement Body',
  title: 'Advertisement Title',
  subtitle: 'Advertisement Sub-description',
  cover_url: 'Advertisement Media File',
  thumbnail_url: 'Advertisement Thumbnail File',
  icon_url: 'Advertisement Logo File',
  cta: 'Advertisement CTA',
  landing_url: 'Landing URL / NW Post URL',
  landing_page_type: 'Landing Page',
  lead_form: 'Lead Form',
  media_type: 'Media Type',
  cap_enabled: 'Advertisement Cap',
  language: 'Language',
  splash_media_duration: 'Media Duration',
  splash_media_no_skip_duration: 'No Skip Duration',
  carousel_images: 'Carousel Landing URL / Media File (min 4)',
  image_slider_cover_image: 'Image Slider Cover Landing URL / Media File',
  image_slider_images: 'Image Slider Landing URL / Media File (min 2)',
  video_slider_cover_video: 'Video Slider Cover Landing URL / Media File',
  video_slider_images: 'Video Slider Landing URL / Media File (min 2)',
};

function adwavValidateValues(type, object, edit = false) {
  let errors = [];
  const exclude = [
    'subtitle',
    'title',
    'impressions_per_day',
    'targeted_states',
    'targeted_interests',
    'targeted_devices',
    'thumbnail_url',
    'html_ad_type',
  ];
  const skipText = ['spotlight', 'splash', 'sponsored'];
  const skipMedia = ['spotlight', 'bigcard', 'sponsored'];
  const skipLeadForm = ['url', 'nw_post'];
  if (type === 'campaign') {
    exclude.push('description');
    if (object.ads_type === 'splash') {
      exclude.push('targeted_feeds');
    }
  } else if (type === 'ads') {
    if (object.type) {
      if (skipText.includes(object.type)) {
        exclude.push('title');
        exclude.push('body');
        if (object.type !== 'sponsored') {
          exclude.push('icon_url');
        }
        object.body = object.type;
      }
      if (skipMedia.includes(object.type)) {
        exclude.push('splash_media_duration');
        exclude.push('splash_media_no_skip_duration');
        exclude.push('cap_enabled');
      }
      if (object.type === 'sponsored') {
        exclude.push('cover_url');
        exclude.push('cta');
        object.media_type = 'image';
        object.cover_url = object.icon_url;
        object.cta = 'sponsored';
      }
      // html splash ad
      if (object.type === 'splash' && object.media_type === 'web') {
        exclude.push('cover_url');
        exclude.push('landing_url');
      } else {
        exclude.push('ad_media');
      }
    }
    if (skipLeadForm.includes(object.landing_page_type)) {
      exclude.push('lead_form');
      object.lead_form = null;
    } else {
      exclude.push('landing_url');
      object.landing_url = null;
    }
  }
  const checkLength = [
    'targeted_feeds',
    'targeted_states',
    'targeted_interests',
    'targeted_telcos',
    'targeted_devices',
    'language',
  ];
  Object.entries(object).forEach(([prop, val]) => {
    if (!exclude.includes(prop)) {
      if (checkLength.includes(prop) && val.length === 0) {
        errors.unshift(prop);
      } else if (prop === 'start_date') {
        if (!edit) {
          if (isBefore(parseISO(val), parseISO(now))) {
            errors.unshift(prop);
          } else if (val === '') {
            errors.unshift(prop);
          }
        } else {
          if (val === '') {
            errors.unshift(prop);
          }
        }
      } else if (prop === 'end_date') {
        if (isBefore(parseISO(val), parseISO(object.start_date))) {
          errors.unshift(prop);
        } else if (val === '') {
          errors.unshift(prop);
        }
      } else if (prop === 'lead_form' && (val === null || val === '{}')) {
        errors.unshift(prop);
      } else if (prop === 'landing_url') {
        if (
          object.landing_page_type === 'url' &&
          !(val.startsWith('http://') || val.startsWith('https://'))
        ) {
          errors.unshift(prop);
        } else if (
          object.landing_page_type === 'nw_post' &&
          !checkPostUrl(val)
        ) {
          errors.unshift(prop);
        }
      } else if (prop === 'ad_media') {
        errors = errors.concat(
          retrieveInvalidHtmlAdMetadataFields(object.html_ad_type, val)
        );
      } else if (val === '') {
        errors.unshift(prop);
      }
    }
  });
  if (errors.length > 0) handleFailedErrors(type, errors);
  return errors.length === 0;
}

function retrieveInvalidHtmlAdMetadataFields(htmlAdType, value) {
  let errors = [];
  if (htmlAdType === HTML_AD_TYPES.CAROUSEL.value) {
    if (
      value.length === 0 ||
      validateHtmlAdMetadata(HTML_AD_TYPES.CAROUSEL.validation, value) === false
    ) {
      errors.push('carousel_images');
    }
  } else if (htmlAdType === HTML_AD_TYPES.IMAGE_SLIDER.value) {
    if (validateHtmlAdMetadataMediaLandingUrl([value[0]]) === false) {
      errors.push('image_slider_cover_image');
    }
    if (
      value.slice(1).length === 0 ||
      validateHtmlAdMetadata(
        HTML_AD_TYPES.IMAGE_SLIDER.validation,
        value.slice(1)
      ) === false
    ) {
      errors.push('image_slider_images');
    }
  } else if (htmlAdType === HTML_AD_TYPES.VIDEO_SLIDER.value) {
    if (validateHtmlAdMetadataMediaLandingUrl([value[0]]) === false) {
      errors.push('video_slider_cover_video');
    }
    if (
      value.slice(1).length === 0 ||
      validateHtmlAdMetadata(
        HTML_AD_TYPES.VIDEO_SLIDER.validation,
        value.slice(1)
      ) === false
    ) {
      errors.push('video_slider_images');
    }
  }
  return errors;
}

function validateHtmlAdMetadata(validation, images) {
  return (
    images.length >= validation.min_images &&
    validateHtmlAdMetadataMediaLandingUrl(images)
  );
}

function validateHtmlAdMetadataMediaLandingUrl(images) {
  return (
    images.filter((image) => {
      return (
        image.media_url === '' ||
        image.landing_url === '' ||
        !(
          image.landing_url.startsWith('http://') ||
          image.landing_url.startsWith('https://')
        )
      );
    }).length === 0
  );
}

function checkPostUrl(val) {
  // eslint-disable-next-line no-useless-escape
  let regex = /^https\:\/\/(dev\.)?newswav.com/i;
  if (process.env.VUE_APP_APP_ENV != 'production') {
    // eslint-disable-next-line no-useless-escape
    regex = /^https:\/\/dev-website\.newswav.dev/i;
  }
  return regex.test(val);
}

function handleFailedErrors(type, errors) {
  let html = "<ul style='text-align: left'>";
  const followMap = type === 'campaign' ? campaignFieldsMap : adFieldsMap;
  errors.reverse().forEach((item) => {
    html += `<li>${followMap[item]}</li>`;
  });
  html += '</ul>';
  Swal.fire({
    icon: 'warning',
    title: 'Please check the following fields',
    html: html,
    showConfirmButton: 'OK',
  });
}

function validateMediaTypeWithFileType(mediaType, file) {
  return (
    mediaType.toLowerCase() ===
    getFileType(file.name.split('.').pop().toLowerCase())
  );
}

function getFileType(fileType) {
  if (VIDEO_TYPES.includes(fileType)) return VIDEO_TYPE;
  if (IMAGE_TYPES.includes(fileType)) return IMAGE_TYPE;

  return 'unknown';
}

export { adwavValidateValues, validateMediaTypeWithFileType };
